import { PropsWithChildren } from 'react';

import { Button, forwardRef, HStack, rem, StyleProps } from '@bonusly/design-system';

import { useActionInput } from '../../context';
import { useTagState } from './hooks/use-tag-state';
import { TagButtonIcon } from './tag-button-icon';
import { Tag } from './types';
import { buttonStyle } from './utils/button-style';

export interface TagButtonProps extends PropsWithChildren, StyleProps {
	tag: Tag;
}

export const TagButton = forwardRef<TagButtonProps, 'button'>(({ tag, ...rest }: TagButtonProps, ref) => {
	const { editor } = useActionInput();
	const { isActive, isEnabled, schema } = useTagState(tag);
	const { startDraft } = useActionInput();

	if (!isEnabled) {
		return null;
	}

	const handleClick = () => {
		if (editor) {
			startDraft();
			editor.chain().insertContent(schema.trigger).focus().run();
		}
	};

	return (
		<Button
			bg="secondary.bgAlpha"
			borderRadius={rem(22)}
			colorScheme="secondary"
			color="text.default"
			fontSize={{ base: 'sm', xs: 'md' }}
			h={{ base: rem(36), xs: rem(44) }}
			isActive={isActive}
			onClick={handleClick}
			pl={{ base: 4, xs: 2 }}
			ref={ref}
			variant="alpha"
			{...buttonStyle(schema)}
			{...rest}
		>
			<HStack spacing={{ base: 1, xs: 2 }}>
				<TagButtonIcon tag={tag} />
				<>{schema.label}</>
			</HStack>
		</Button>
	);
});

TagButton.displayName = 'TagButton';
